import {
  type PropsWithChildren,
  createContext,
  useContext,
  useMemo,
  useState,
} from 'react';

import { useAsyncEffect } from '@hivebrite/react-hooks';

import type { DynamicImportComponentDetail } from 'hb-builder/types';
import {
  type LoadedComponents,
  loadComponents,
} from 'hb-builder/utils/componentsLoader';

interface ComponentsContextType {
  components: LoadedComponents;
}

const ComponentsContext = createContext<ComponentsContextType | undefined>(
  undefined,
);

export interface ComponentsProviderProps {
  components: DynamicImportComponentDetail[];
}

export const ComponentsProvider = ({
  children,
  components,
}: PropsWithChildren<ComponentsProviderProps>) => {
  const [componentsState, setComponents] = useState<LoadedComponents>();

  useAsyncEffect(async () => {
    const loadedComponents = await loadComponents(components);

    loadedComponents && setComponents(loadedComponents);
  }, [components]);

  const value: ComponentsContextType | undefined = useMemo(
    () => (componentsState ? { components: componentsState } : undefined),
    [componentsState],
  );

  if (!value) {
    return null;
  }

  return (
    <ComponentsContext.Provider value={value}>
      {children}
    </ComponentsContext.Provider>
  );
};

export const useComponentsContext = () => {
  const context = useContext(ComponentsContext);

  if (!context) {
    throw new Error(
      'useComponentsContext must be used within a ComponentsProvider',
    );
  }

  return context;
};
